import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../auth';
import { Subscription, SubscriptionService } from '../../notifications/subscriptions/subscription.service';

/**
 * The reader's subscription to a show: one button that subscribes, with in-app and email both on,
 * or unsubscribes. Each episode the show releases then reaches the reader by those switches, which
 * they change on the subscription screen. A reader who is not signed in is sent to
 * log in, and brought back to this page.
 */
@Component({
  selector: 'multisite-shows-subscribe-button',
  imports: [RouterModule, TranslocoModule],
  providers: [provideTranslocoScope('account')],
  template: `
    @if (known()) {
      <ng-container *transloco="let t">
        <button
          type="button"
          class="btn btn-sm"
          [class.btn-primary]="subscription()"
          [class.btn-outline-primary]="!subscription()"
          [disabled]="busy()"
          (click)="toggle()">
          <i class="fa-bell mr-1" [class.fa-solid]="subscription()" [class.fa-regular]="!subscription()"></i>
          {{ subscription() ? t('account.subscriptions.unsubscribe') : t('account.subscriptions.subscribe') }}
        </button>
        @if (subscription()) {
          <a class="btn btn-link btn-sm" routerLink="/account/subscriptions">{{ t('common.manage') }}</a>
        }
        @if (failed()) {
          <small class="d-block text-danger mt-1" role="alert">{{ t('error.not_done_try_again') }}</small>
        }
      </ng-container>
    }
  `,
})
export class ShowsSubscribeButtonComponent {
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);
  private router = inject(Router);

  /** The show's slug. */
  show = input.required<string>();

  /**
   * The signed-in user. Undefined both while the user loads and for a reader with no token, for
   * whom the application never loads one; null when loading failed.
   */
  readonly user = toSignal(this.authService.user);
  /** Whether the page knows who the reader is, so that the button cannot flicker from one state to the other. */
  readonly known = computed(() => this.user() !== undefined || !this.authService.token);
  readonly subscription = signal<Subscription | null>(null);
  readonly busy = signal(false);
  readonly failed = signal(false);

  constructor() {
    effect(() => {
      const show = this.show();

      this.subscription.set(null);
      this.failed.set(false);

      if (!this.user()) {
        return;
      }

      this.subscriptionService.getSubscriptions().subscribe({
        next: (subscriptions) =>
          this.subscription.set(subscriptions.find((s) => s.criterion?.type === 'shows' && s.criterion.id === show) ?? null),
        error: () => this.subscription.set(null),
      });
    });
  }

  toggle(): void {
    if (this.busy()) {
      return;
    }

    if (!this.user()) {
      this.authService.setCurrentRouteForRedirectAfterAuth();
      this.router.navigate(['/login']);
      return;
    }

    const current = this.subscription();
    const request: Observable<Subscription | null> = current
      ? this.subscriptionService.unsubscribe(current.id).pipe(map(() => null))
      : this.subscriptionService.subscribe({ type: 'shows', id: this.show() });

    this.busy.set(true);
    this.failed.set(false);
    request.subscribe({
      next: (subscription) => {
        this.subscription.set(subscription);
        this.busy.set(false);
      },
      error: () => {
        this.failed.set(true);
        this.busy.set(false);
      },
    });
  }
}
